"use client"

// P-147 (SPEC-102 Task A — CRÍTICA): React #418 hydration mismatch ×9 por
// página en TODAS las rutas (incl. estáticas /offline, /legal/terminos —
// stack idéntico → chunk compartido del ROOT LAYOUT).
//
// Análisis (estático + empírico, ver worklog Task 2-c):
//   1. Este wrapper pasa `theme={useTheme().theme}`. next-themes lee
//      localStorage DURANTE el primer render del cliente (theme: 'dark' |
//      'light' | undefined en SSR) — divergencia server vs client.
//   2. sonner (dist/index.mjs ~L1005) inicializa `actualTheme` con un
//      useState INITIALIZER que lee `window.matchMedia('(prefers-color-
//      scheme: dark)')` DURANTE el render, y renderiza
//      `data-sonner-theme={actualTheme}` + `dir={getDocumentDirection()}`
//      (lee document.documentElement) en el `<ol data-sonner-toaster>`.
//   3. Con CERO toasts el SSR y el primer render client coinciden (section
//      vacía — verificado en staging + build local). PERO el estado de
//      toasts de sonner (ToastState) es a NIVEL DE MÓDULO: si algún toast
//      se encola antes de que termine la hidratación (sesiones QA largas:
//      toasts de 401/red/SW disparados durante la carga de chunks), el
//      primer render client materializa el `<ol data-sonner-toaster
//      data-sonner-theme=...>` que el HTML del servidor NO tiene → React
//      #418 en TODA ruta (el <Toaster/> vive en el root layout).
//
// Fix: mount-gate (patrón documentado por sonner para Next.js App Router;
// mismo patrón que theme-toggle.tsx, InstallBanner.tsx y OfflineIndicator).
// No renderizar NADA hasta terminar la hidratación → elimina TODOS los
// vectores de divergencia de una vez. Los toasts solo se disparan por
// interacción del usuario (post-mount) y los encolados pre-mount quedan en
// ToastState hasta que el Toaster se monta → sin pérdida de toasts. El dark
// mode de los toasts queda intacto (post-mount useTheme() retorna el tema
// real del usuario).
import { useEffect, useState } from "react"
import { useTheme } from "next-themes"
import { Toaster as Sonner, ToasterProps } from "sonner"

const Toaster = ({ ...props }: ToasterProps) => {
  const [mounted, setMounted] = useState(false)
  const { theme = "system" } = useTheme()

  useEffect(() => {
    setMounted(true)
  }, [])

  // Render nothing on the server AND on the hydration render — the Toaster
  // only materializes after mount (post-hydration). This guarantees the
  // hydration-time output is deterministically `null` (no matchMedia /
  // useTheme / ToastState reads can diverge from the empty SSR output).
  if (!mounted) return null

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      style={
        {
          "--normal-bg": "var(--popover)",
          "--normal-text": "var(--popover-foreground)",
          "--normal-border": "var(--border)",
        } as React.CSSProperties
      }
      {...props}
    />
  )
}

export { Toaster }
